import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { textColors } from '@/mocks/app-data';
import {
  type AspectRatio,
  type LogoOverlay,
  type LogoTint,
  type ShapeOverlay,
  type SuggestedFrame,
  type TextOverlay,
  REFERENCE_CANVAS_WIDTH,
  applyPreset,
  aspectClass,
  aspectToRefHeight,
  autoAdjustOverlayColors,
  clampAnchor,
  flattenComposedFrame,
  getTextLayerStyle,
  resolveStackedLayers,
} from '../lib/composeRender';
import { TEXT_PRESETS } from '../lib/textPresets';
import PresetThumbnail from './PresetThumbnail';
import StepHeader from './StepHeader';
import { savePost } from '@/lib/gallery';
import { useCredits } from '@/hooks/useCredits';
import { zip } from 'fflate';

export type { TextOverlay };

interface StepTextProps {
  platformAspectRatio: string;
  templateId: string;
  isCarousel: boolean;
  overlays: TextOverlay[];
  onOverlaysChange: (overlays: TextOverlay[]) => void;
  shapeOverlays: ShapeOverlay[];
  onShapeOverlaysChange: (shapes: ShapeOverlay[]) => void;
  /** Brand logo overlays — separate from text/shape so the renderer keeps
   *  its synchronous Layer path while logos handle their own async image
   *  preload. Optional; when both this AND brandLogoUrl are absent the
   *  "Add logo" affordance hides entirely. */
  logoOverlays?: LogoOverlay[];
  onLogoOverlaysChange?: (logos: LogoOverlay[]) => void;
  /** Public URL of the user's brand logo (from /api/brand). When null,
   *  the editor shows a "Set up in My Brand" empty-state prompt instead
   *  of the Add Logo button. */
  brandLogoUrl?: string | null;
  selectedPresetId: string | null;
  onPresetSelect: (id: string | null) => void;
  generatedImages: string[];
  suggestedFrames: SuggestedFrame[];
  // Refetch AI copy; returns the fresh frames so we can re-apply the active
  // preset against the new headline/tagline. Null on failure.
  onRegenerateCopy: () => Promise<SuggestedFrame[] | null>;
  onBack: () => void;
  /** When true, skip the StepHeader and lock the workspace to a fixed
   *  viewport-relative height so the 3-col layout's internal scrolls work
   *  in /appv2's flowing page (vs /app's fixed-viewport panel). */
  compact?: boolean;
  /** Embedded mode (Jun 12 — /appv3 Studio render panel): no StepHeader,
   *  no footer (the host supplies its own action row + prompt-edit bar),
   *  h-full root so the 3-col workspace fills whatever fixed-height box
   *  the host provides. Mutually exclusive with `compact` — pass one. */
  embedded?: boolean;
  /** Fired whenever the active frame changes (carousel thumbnail click).
   *  The Studio's prompt-edit box targets the active frame, so the host
   *  needs to track it. */
  onActiveImageChange?: (index: number) => void;
  /** Dev-only: when set, render a "Save as Recreate template (dev)" button
   *  next to Download. Captures the active frame at full resolution
   *  (overlays composited) and hands the base64 PNG up to the parent so it
   *  can prompt for a name + POST to /api/recreate-templates-save.
   *
   *  Currently wired from /appv2 only — /app intentionally doesn't get
   *  this button since /app is being retired. The dev nature is signaled
   *  visually with a dashed border + "DEV" badge. */
  onSaveAsRecreate?: (previewPngBase64: string) => Promise<void> | void;
}

export default function StepText({
  platformAspectRatio,
  templateId,
  isCarousel,
  overlays,
  onOverlaysChange,
  shapeOverlays,
  onShapeOverlaysChange,
  logoOverlays = [],
  onLogoOverlaysChange,
  brandLogoUrl = null,
  selectedPresetId,
  onPresetSelect,
  generatedImages,
  suggestedFrames,
  onRegenerateCopy,
  onBack,
  compact,
  embedded,
  onActiveImageChange,
  onSaveAsRecreate,
}: StepTextProps) {
  // Pass through any supported aspect; unknown ones fall back to 1:1 so
  // the canvas + text overlay math always has a defined ratio.
  const SUPPORTED_ASPECTS: ReadonlySet<string> = new Set(['1:1', '4:5', '2:3', '9:16', '1.91:1', '16:9']);
  const aspectRatio: AspectRatio = (SUPPORTED_ASPECTS.has(platformAspectRatio)
    ? platformAspectRatio
    : '1:1') as AspectRatio;
  const frameCount = isCarousel ? 4 : 1;

  // Free-tier locks: Save to Gallery stays enabled (build a portfolio they
  // can unlock later); Download is blocked until upgrade — instead the button
  // turns into an "Upgrade to Download" CTA that routes to checkout.
  const navigate = useNavigate();
  const { planTier } = useCredits();
  const isFree = planTier === 'free';

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  // Surface frame changes to embedded hosts (the Studio's prompt-edit box
  // targets the active frame).
  useEffect(() => {
    onActiveImageChange?.(activeImageIndex);
    // Callback identity changes shouldn't re-fire — index is the signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImageIndex]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Live preview canvas width (px). Used to compute the font scale so DOM text
  // wraps at the same point the download canvas will — preview ≡ output.
  const [previewW, setPreviewW] = useState(0);

  // Refs to avoid stale closures inside long-lived drag handlers
  const overlaysRef = useRef(overlays); overlaysRef.current = overlays;
  const logoOverlaysRef = useRef(logoOverlays); logoOverlaysRef.current = logoOverlays;
  const activeIdRef = useRef(activeId); activeIdRef.current = activeId;
  // True when the in-flight drag is on a logo (vs a text layer). Set on
  // mousedown of a logo node, cleared on mouseup. Lets the shared drag
  // effect dispatch to the right state setter.
  const draggingLogoRef = useRef(false);
  const previewRef = useRef<HTMLDivElement>(null);
  // Captured at drag-start: box dims (px) of the dragged element. Used to clamp
  // by the rendered box bounds (not the anchor point alone) so the text block
  // can't overflow the canvas.
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  // Fractional offset from the cursor at grab-time to the overlay's anchor.
  // Subtracted from the cursor position during drag so the text moves with
  // the mouse instead of teleporting its anchor to wherever you clicked.
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  // ---- Drag-to-position for text overlays ----
  useEffect(() => {
    if (!draggingId) return;
    const updateFromPoint = (clientX: number, clientY: number) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      // Logo path — logos use a simpler top-left anchor model (no 9-anchor
      // grid) so we clamp directly with the box dims instead of going
      // through clampAnchor's anchor math. Width is fractional, height
      // is derived from the rendered DOM node's dimensions.
      if (draggingLogoRef.current && onLogoOverlaysChange) {
        const logo = logoOverlaysRef.current.find((l) => l.id === draggingId);
        if (!logo) return;
        const cursorX = (clientX - rect.left) / rect.width;
        const cursorY = (clientY - rect.top) / rect.height;
        const off = dragOffsetRef.current;
        const targetX = off ? cursorX - off.dx : cursorX;
        const targetY = off ? cursorY - off.dy : cursorY;
        const box = dragBoxRef.current;
        const wFrac = box ? box.w / rect.width : logo.widthFrac;
        const hFrac = box ? box.h / rect.height : logo.widthFrac;
        // Clamp top-left so the box fits inside [0, 1−size].
        const clampedX = Math.max(0, Math.min(1 - wFrac, targetX));
        const clampedY = Math.max(0, Math.min(1 - hFrac, targetY));
        onLogoOverlaysChange(
          logoOverlaysRef.current.map((l) =>
            l.id === draggingId ? { ...l, x: clampedX, y: clampedY } : l,
          ),
        );
        return;
      }

      // Text path — original behavior.
      const dragged = overlaysRef.current.find((o) => o.id === draggingId);
      if (!dragged) return;
      const cursorX = (clientX - rect.left) / rect.width;
      const cursorY = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      // Subtract the grab-time offset so the anchor stays the same distance
      // from the cursor as when the user grabbed the text. No more jump.
      const targetX = off ? cursorX - off.dx : cursorX;
      const targetY = off ? cursorY - off.dy : cursorY;
      const box = dragBoxRef.current;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const { x, y } = clampAnchor(targetX, targetY, wFrac, hFrac, dragged.anchor);
      onOverlaysChange(
        overlaysRef.current.map((o) =>
          o.id === draggingId ? { ...o, x, y, positionId: '' } : o,
        ),
      );
    };
    const onMouseMove = (e: MouseEvent) => updateFromPoint(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) updateFromPoint(t.clientX, t.clientY);
    };
    const onUp = () => {
      setDraggingId(null);
      dragBoxRef.current = null;
      dragOffsetRef.current = null;
      draggingLogoRef.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId, onOverlaysChange, onLogoOverlaysChange]);

  // ---- Track preview canvas width for accurate font scaling ----
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Compact mode: anchor left + right column heights to the middle col
  // (the image canvas + chrome). In /appv2 we want the whole block to hug
  // the natural preview height instead of stretching to viewport. ----
  const middleColRef = useRef<HTMLDivElement>(null);
  const [middleColH, setMiddleColH] = useState<number | null>(null);
  useEffect(() => {
    if (!compact) return;
    const el = middleColRef.current;
    if (!el) return;
    const update = () => setMiddleColH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact]);

  // Match the preview to the eventual output canvas: scale = previewW / reference.
  // Falls back to 1 before the observer fires (one render frame at most).
  const previewFontScale = previewW > 0 ? previewW / REFERENCE_CANVAS_WIDTH : 1;

  const updateOverlay = (id: string, updates: Partial<TextOverlay>) => {
    onOverlaysChange(overlaysRef.current.map((o) => (o.id === id ? { ...o, ...updates } : o)));
  };

  const removeOverlay = (id: string) => {
    onOverlaysChange(overlaysRef.current.filter((o) => o.id !== id));
    if (activeIdRef.current === id) setActiveId(null);
  };

  // ---- Logo handlers ----

  const addLogoForCurrentFrame = () => {
    if (!brandLogoUrl || !onLogoOverlaysChange) return;
    // Default: bottom-right corner, 18% canvas width — canonical brand-
    // mark spot. User drags to reposition.
    const defaultW = 0.18;
    // y will be re-clamped on first drag; for now position 16% from the
    // bottom to leave a visible margin. Logo's natural aspect is unknown
    // until the image loads, so we use widthFrac as both width and
    // height-anchor (conservative — gets corrected on first drag).
    const next: LogoOverlay = {
      id: `logo-${Date.now()}`,
      imageIndex: activeImageIndex,
      src: brandLogoUrl,
      x: 1 - defaultW - 0.04,
      y: 1 - defaultW - 0.04,
      widthFrac: defaultW,
      opacity: 1,
      tint: 'original',
    };
    onLogoOverlaysChange([...logoOverlaysRef.current, next]);
    setActiveId(next.id);
  };

  const updateLogo = (id: string, updates: Partial<LogoOverlay>) => {
    if (!onLogoOverlaysChange) return;
    onLogoOverlaysChange(
      logoOverlaysRef.current.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    );
  };

  const removeLogo = (id: string) => {
    if (!onLogoOverlaysChange) return;
    onLogoOverlaysChange(logoOverlaysRef.current.filter((l) => l.id !== id));
    if (activeIdRef.current === id) setActiveId(null);
  };

  const setImageIndex = (index: number) => {
    setActiveImageIndex(index);
    setActiveId(null);
  };

  // ---- Preset application ----
  // Applies one preset across all frames at once. Wholesale-replaces both
  // text and shape overlays; any custom additions on top are lost. After the
  // initial paint we asynchronously flip plain-text layers (no backgroundColor)
  // to black or white based on what's actually behind them on the generated
  // image, so dark-on-dark and light-on-light cases self-correct.
  const applyPresetWithFrames = async (presetId: string, frames: SuggestedFrame[]) => {
    const preset = TEXT_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const { texts, shapes } = applyPreset(preset, aspectRatio, frameCount, frames);
    onOverlaysChange(texts);
    onShapeOverlaysChange(shapes);
    onPresetSelect(presetId);
    setActiveId(null);
    // Then sample the image behind each plain-text layer and pick black/white.
    // Done after the initial state update so the preset shows immediately and
    // the contrast pass refines it in the next tick.
    const adjusted = await autoAdjustOverlayColors(texts, generatedImages);
    onOverlaysChange(adjusted);
  };

  const handleApplyPreset = (presetId: string) => applyPresetWithFrames(presetId, suggestedFrames);

  // Refetch the AI copy and, if a preset is already applied, re-render it
  // against the fresh frames. Uses the returned frames directly so we don't
  // wait for the parent state update to round-trip.
  const handleRegenerate = async () => {
    if (isRegenerating) return;
    setIsRegenerating(true);
    try {
      const newFrames = await onRegenerateCopy();
      if (newFrames && selectedPresetId) {
        await applyPresetWithFrames(selectedPresetId, newFrames);
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  // ---- Render a specific frame to a flattened PNG blob ----
  // Shared by single-frame Download, Save to Gallery, and the carousel
  // Download-All zip path. Awaits font loading first so canvas text metrics
  // match the DOM preview exactly (otherwise system-font fallback metrics
  // diverge from Inter etc).
  // Implementation extracted to composeRender.flattenComposedFrame
  // (Jun 12) so the /appv3 Studio render panel flattens with the exact
  // same pixels — this wrapper just binds the component's closure state.
  const flattenFrameToBlob = (index: number): Promise<Blob | null> =>
    flattenComposedFrame({
      imageSrc: generatedImages[index],
      frameIndex: index,
      overlays,
      shapeOverlays,
      logoOverlays,
    });

  // ---- Download (browser download via Blob URL) ----
  const handleDownload = async () => {
    const blob = await flattenFrameToBlob(activeImageIndex);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `post-${templateId}-frame${activeImageIndex + 1}.png`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ---- Save as Recreate template (dev) — only when onSaveAsRecreate is set ----
  // Flattens the active frame the same way Download does, but instead of
  // triggering a download we hand the base64-encoded PNG up to the parent.
  // The parent prompts for a name + POSTs to /api/recreate-templates-save.
  const [recreateSaveStatus, setRecreateSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const handleSaveAsRecreate = async () => {
    if (!onSaveAsRecreate) return;
    if (recreateSaveStatus === 'saving') return;
    setRecreateSaveStatus('saving');
    try {
      const blob = await flattenFrameToBlob(activeImageIndex);
      if (!blob) throw new Error('Could not render preview');
      // Read blob as base64 — FileReader is the simplest cross-browser path.
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error ?? new Error('FileReader failed'));
        r.readAsDataURL(blob);
      });
      await onSaveAsRecreate(base64);
      setRecreateSaveStatus('saved');
      setTimeout(() => setRecreateSaveStatus('idle'), 4000);
    } catch (err) {
      console.error('Save as Recreate failed:', err);
      setRecreateSaveStatus('error');
      setTimeout(() => setRecreateSaveStatus('idle'), 4000);
    }
  };

  // ---- Carousel zip builder (shared by Download All + Save to Gallery) ----
  // Flattens every generated frame (with its own text + shape + logo
  // overlays applied) in parallel, then bundles into a single zip via
  // fflate. Returns the zip as an application/zip Blob. Throws on any
  // per-frame render failure so callers can surface a clear error.
  const buildCarouselZipBlob = async (): Promise<Blob> => {
    // Flatten all frames in parallel — each call awaits its own
    // document.fonts.ready and Image load independently.
    const blobs = await Promise.all(
      generatedImages.map((_, i) => flattenFrameToBlob(i)),
    );
    const entries: Record<string, Uint8Array> = {};
    for (let i = 0; i < blobs.length; i++) {
      const b = blobs[i];
      if (!b) throw new Error(`Could not render frame ${i + 1}`);
      entries[`frame${i + 1}.png`] = new Uint8Array(await b.arrayBuffer());
    }
    const zipped: Uint8Array = await new Promise((resolve, reject) => {
      zip(entries, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
    // ArrayBuffer cast keeps TS happy across DOM lib versions that type
    // Blob's parts as BlobPart (excludes raw Uint8Array in some setups).
    return new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
  };

  // ---- Download all carousel frames as a single zip ----
  // Only surfaced on the Add-Text step when isCarousel is true. Status
  // state mirrors the Save to Gallery pattern so the user gets clear
  // feedback for what is otherwise an opaque 4× canvas-encode
  // operation (~1-3s on most machines).
  const [downloadAllStatus, setDownloadAllStatus] = useState<
    'idle' | 'zipping' | 'error'
  >('idle');
  const handleDownloadAll = async () => {
    if (downloadAllStatus === 'zipping') return;
    setDownloadAllStatus('zipping');
    try {
      const zipBlob = await buildCarouselZipBlob();
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `post-${templateId}-carousel.zip`;
      link.click();
      URL.revokeObjectURL(url);
      setDownloadAllStatus('idle');
    } catch (err) {
      console.error('Download all failed:', err);
      setDownloadAllStatus('error');
      setTimeout(() => setDownloadAllStatus('idle'), 4000);
    }
  };

  // ---- Save to Gallery (upload to Supabase + insert posts row) ----
  // States 'saving' / 'saved' / 'error' surface as button label changes; we
  // briefly hold 'saved'/'error' for a couple seconds before snapping back
  // to 'idle' so the user can re-save the same composition if they want.
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const handleSaveToGallery = async () => {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      if (isCarousel) {
        // Carousel save — bundle all 4 frames (each with its own
        // overlays applied) into a single zip and store it as the
        // post's asset. Stored at gallery/{user_id}/{post_id}.zip
        // with content-type application/zip so a direct download
        // from Supabase Storage returns the zip cleanly.
        // Headline/tagline use frame 1 as representative metadata —
        // it's what the gallery uses for the row title; per-frame
        // copy is preserved inside the zip's PNGs themselves.
        const zipBlob = await buildCarouselZipBlob();
        const frame = suggestedFrames[0];
        await savePost({
          blob: zipBlob,
          templateId,
          aspectRatio,
          headline: frame?.headline ?? null,
          tagline: frame?.tagline ?? null,
          contentType: 'application/zip',
          fileExt: 'zip',
        });
      } else {
        // Single-image save (existing behavior).
        const blob = await flattenFrameToBlob(activeImageIndex);
        if (!blob) throw new Error('Could not render frame');
        const frame = suggestedFrames[activeImageIndex];
        await savePost({
          blob,
          templateId,
          aspectRatio,
          headline: frame?.headline ?? null,
          tagline: frame?.tagline ?? null,
        });
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Save to Gallery failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  // ---- Derived render data ----
  const currentOverlays = overlays.filter((o) => o.imageIndex === activeImageIndex);
  const currentLogos = logoOverlays.filter((l) => l.imageIndex === activeImageIndex);
  const activeLogo = currentLogos.find((l) => l.id === activeId) || null;

  // CSS filter for DOM preview based on logo tint. The corresponding
  // canvas-export path in flattenFrameToBlob uses globalCompositeOperation
  // = 'source-in' for the same effect — see that function for details.
  const logoFilterCss = (tint: LogoTint | undefined): string | undefined => {
    if (tint === 'black') return 'brightness(0)';
    if (tint === 'white') return 'brightness(0) invert(1)';
    return undefined;
  };
  const currentShapes = shapeOverlays.filter((s) => s.imageIndex === activeImageIndex);
  // Resolve live stacking against the *current* (possibly user-edited) text.
  // Stacked layers get a fresh `y` here so the preview reflows when copy
  // changes — the canvas renderer does the same pass at draw time, so DOM
  // and download stay aligned.
  const refH = aspectToRefHeight(aspectRatio);
  const resolvedCurrent = useMemo(
    () => resolveStackedLayers(currentOverlays, REFERENCE_CANVAS_WIDTH, refH) as TextOverlay[],
    [currentOverlays, refH],
  );
  const activeOverlay = currentOverlays.find((o) => o.id === activeId) || null;
  const activeImage = generatedImages[activeImageIndex] || '';
  const hasImages = generatedImages.length === frameCount;
  // A preset is available if it has an aspect-specific layout OR a '1:1'
  // layout we can fall back to (applyPreset handles the fallback). This
  // makes every existing 1:1 preset available on every aspect.
  const availablePresets = TEXT_PRESETS.filter((p) => p.layouts[aspectRatio] || p.layouts['1:1']);

  // Workspace root height. /app's wizard panel already constrains height
  // via h-screen + overflow-hidden, so h-full is enough. /appv2 is a
  // flowing page, so we lock the workspace to a viewport-relative height
  // here — that lets the 3-col internal flex math (overflow-y-auto on the
  // preset gallery, image canvas filling the middle col, layer panel
  // scrolling on the right) behave exactly the same. min-h floor keeps
  // short windows usable.
  const rootClass = compact
    ? 'flex flex-col'
    : 'h-full flex flex-col';

  if (!hasImages) {
    return (
      <div className={rootClass}>
        {!compact && !embedded && (
          <StepHeader
            icon="ri-text"
            title="Missing Generated Images"
            subtitle="We don't have generated frames to overlay text onto. Step back and regenerate first."
          />
        )}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Back to Preview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {!compact && !embedded && (
        <StepHeader
          icon="ri-text"
          title="Add Text Overlays"
          subtitle="Pick a preset to apply text styling, then drag layers on the canvas to fine-tune their position."
        />
      )}
      {/* Responsive body. 3-col grid only at xl (≥1280px) — below that we
          stack vertically and let this container scroll, since AppLayout's
          <main> is overflow-hidden and won't paginate the page itself.
          In compact mode (/appv2) the block hugs the middle col's natural
          height (image + chrome) — left + right cols receive an explicit
          height in JS, so xl:items-start keeps them from stretching. */}
      <div className={
        compact
          ? 'flex flex-col gap-4 xl:grid xl:grid-cols-12 xl:gap-4 xl:items-start'
          : 'flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto pr-1 xl:grid xl:grid-cols-12 xl:gap-4 xl:overflow-hidden xl:pr-0'
      }>
        {/* LEFT — preset gallery. xl: vertical 2-col grid. Below: horizontal scroll strip. */}
        <div
          className="
            xl:col-span-3 xl:min-h-0
            flex flex-col rounded-2xl bg-white border border-surface-dark/40 p-4
          "
          style={compact && middleColH ? { height: middleColH } : undefined}
        >
          <div className="flex-shrink-0 flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-dark text-xs">Text Presets</h3>
            <span className="text-xs text-dark-muted">
              {availablePresets.length} for {aspectRatio}
            </span>
          </div>
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="
              group relative flex-shrink-0 mb-3 w-full inline-flex items-center justify-center gap-1.5
              px-3 py-2 rounded-lg overflow-hidden
              bg-gradient-to-r from-primary via-fuchsia-500 to-accent
              text-white text-xs font-bold tracking-wide
              shadow-md shadow-primary/25
              hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-px
              active:translate-y-0
              transition-all duration-200 cursor-pointer
              disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
            "
          >
            {/* Subtle shimmer sweep on hover */}
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
            <i className={`${isRegenerating ? 'ri-loader-4-line animate-spin' : 'ri-sparkling-2-line'} text-sm relative`} />
            <span className="relative">{isRegenerating ? 'Regenerating…' : 'Regenerate with AI'}</span>
          </button>
          {availablePresets.length > 0 ? (
            <>
              {/* xl: vertical scrolling 2-col grid */}
              <div className="hidden xl:block xl:flex-1 xl:min-h-0 xl:overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-2">
                  {availablePresets.map((preset) => (
                    <PresetThumbnail
                      key={preset.id}
                      preset={preset}
                      aspectRatio={aspectRatio}
                      isSelected={selectedPresetId === preset.id}
                      onClick={() => handleApplyPreset(preset.id)}
                      backgroundImage={activeImage}
                      suggestedFrame={suggestedFrames[activeImageIndex]}
                    />
                  ))}
                </div>
              </div>
              {/* Below xl: horizontal scroll strip — compact, doesn't eat vertical space.
                  Each tile is a fixed width; height comes from the canvas's aspect ratio. */}
              <div className="xl:hidden flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {availablePresets.map((preset) => (
                  <div key={preset.id} className="flex-shrink-0 w-28 sm:w-32">
                    <PresetThumbnail
                      preset={preset}
                      aspectRatio={aspectRatio}
                      isSelected={selectedPresetId === preset.id}
                      onClick={() => handleApplyPreset(preset.id)}
                      backgroundImage={activeImage}
                      suggestedFrame={suggestedFrames[activeImageIndex]}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-32 xl:h-auto xl:flex-1 flex items-center justify-center rounded-xl border border-dashed border-surface-dark/40 bg-surface/40">
              <p className="text-xs text-dark-muted px-4 text-center">No presets available for {aspectRatio} yet.</p>
            </div>
          )}
        </div>

        {/* MIDDLE — preview canvas. In compact mode this column is the
            height anchor for the row (left + right match via inline style). */}
        <div
          ref={middleColRef}
          className="
            xl:col-span-5 xl:min-h-0
            flex flex-col rounded-2xl bg-white border border-surface-dark/40 p-4
          "
        >
          <div className="flex-shrink-0 flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-dark text-xs">
              {isCarousel ? `Image ${activeImageIndex + 1} of ${frameCount}` : 'Live Preview'}
            </h3>
            <span className="text-xs text-dark-muted">
              {currentOverlays.length} layer{currentOverlays.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="xl:flex-1 xl:min-h-0 flex items-center justify-center">
            <div
              ref={previewRef}
              className={`relative rounded-xl overflow-hidden border border-surface-dark/20 bg-surface max-w-xl w-full ${aspectClass(aspectRatio)}`}
            >
              {activeImage ? (
                <img src={activeImage} alt="Generated post" className="w-full h-full object-cover" draggable={false} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-dark-muted text-sm">No image</p>
                </div>
              )}

              {/* Shapes (decorative; render under text). Not interactive in v1. */}
              {currentShapes.map((shape) => (
                <div
                  key={shape.id}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${shape.x * 100}%`,
                    top: `${shape.y * 100}%`,
                    width: `${shape.w * 100}%`,
                    height: `${shape.h * 100}%`,
                    backgroundColor: shape.color,
                    opacity: shape.opacity,
                    borderRadius: shape.shape === 'circle' ? '50%' : undefined,
                    transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
                  }}
                />
              ))}

              {/* Anchor-positioned, draggable text overlays. We render the
                  *resolved* overlay (post-stacking) so the visible position
                  matches what the canvas renderer will emit on download. */}
              {resolvedCurrent.map((overlay) => (
                <div
                  key={overlay.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const previewRect = previewRef.current?.getBoundingClientRect();
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                      const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                      const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                      // Use the *resolved* y so a stacked layer's drag offset
                      // matches its visible position, not its stale stored y.
                      dragOffsetRef.current = {
                        dx: cursorX - overlay.x,
                        dy: cursorY - overlay.y,
                      };
                    }
                    // If this layer was stacked, bake the resolved y into the
                    // stored state and clear the stack flag — the user is
                    // taking manual control of its position.
                    if (overlay.stackOnPrevious) {
                      onOverlaysChange(overlaysRef.current.map((o) =>
                        o.id === overlay.id
                          ? { ...o, y: overlay.y, stackOnPrevious: false }
                          : o,
                      ));
                    }
                    setActiveId(overlay.id);
                    setDraggingId(overlay.id);
                  }}
                  onTouchStart={(e) => {
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const previewRect = previewRef.current?.getBoundingClientRect();
                    const t = e.touches[0];
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                      const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                      const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                      dragOffsetRef.current = {
                        dx: cursorX - overlay.x,
                        dy: cursorY - overlay.y,
                      };
                    }
                    if (overlay.stackOnPrevious) {
                      onOverlaysChange(overlaysRef.current.map((o) =>
                        o.id === overlay.id
                          ? { ...o, y: overlay.y, stackOnPrevious: false }
                          : o,
                      ));
                    }
                    setActiveId(overlay.id);
                    setDraggingId(overlay.id);
                  }}
                  className={`absolute select-none ${
                    draggingId === overlay.id ? 'cursor-grabbing' : 'cursor-grab'
                  } ${
                    activeId === overlay.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
                  }`}
                  style={getTextLayerStyle(overlay, { previewFontScale })}
                >
                  {overlay.text}
                </div>
              ))}

              {/* Brand-logo overlays — drawn LAST (above text) so the
                  brand mark is never hidden by a text layer. Drag uses
                  the same shared handler as text via the
                  draggingLogoRef switch. */}
              {currentLogos.map((logo) => (
                <img
                  key={logo.id}
                  src={logo.src}
                  alt="Brand logo"
                  draggable={false}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const previewRect = previewRef.current?.getBoundingClientRect();
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                      const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                      const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                      // Logo uses top-left anchor, not 9-anchor — offset is
                      // measured from the logo's stored top-left position
                      // (logo.x / logo.y).
                      dragOffsetRef.current = {
                        dx: cursorX - logo.x,
                        dy: cursorY - logo.y,
                      };
                    }
                    draggingLogoRef.current = true;
                    setActiveId(logo.id);
                    setDraggingId(logo.id);
                  }}
                  onTouchStart={(e) => {
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const previewRect = previewRef.current?.getBoundingClientRect();
                    const t = e.touches[0];
                    dragBoxRef.current = { w: r.width, h: r.height };
                    if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                      const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                      const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                      dragOffsetRef.current = {
                        dx: cursorX - logo.x,
                        dy: cursorY - logo.y,
                      };
                    }
                    draggingLogoRef.current = true;
                    setActiveId(logo.id);
                    setDraggingId(logo.id);
                  }}
                  className={`absolute select-none ${
                    draggingId === logo.id ? 'cursor-grabbing' : 'cursor-grab'
                  } ${
                    activeId === logo.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
                  }`}
                  style={{
                    left: `${logo.x * 100}%`,
                    top: `${logo.y * 100}%`,
                    width: `${logo.widthFrac * 100}%`,
                    height: 'auto',
                    opacity: logo.opacity,
                    filter: logoFilterCss(logo.tint),
                    pointerEvents: 'auto',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Carousel tabs */}
          {isCarousel && (
            <div className="flex-shrink-0 mt-3">
              <div className="flex items-center justify-center gap-2">
                {generatedImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImageIndex(i)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      activeImageIndex === i ? 'border-primary shadow-md' : 'border-transparent hover:border-surface-dark/50'
                    }`}
                  >
                    <img src={img} alt={`Frame ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-xs font-bold ${activeImageIndex === i ? 'text-primary' : 'text-white drop-shadow-md'}`}>
                        {i + 1}
                      </span>
                    </div>
                    {overlays.some((o) => o.imageIndex === i) && (
                      <div className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-accent" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — layer list (top) + edit controls (bottom). */}
        <div
          className="
            xl:col-span-4 xl:min-h-0
            flex flex-col gap-3
          "
          style={compact && middleColH ? { height: middleColH } : undefined}
        >
          {/* Brand logo affordance. Visible only when the parent passes
              the onLogoOverlaysChange handler (i.e. /appv2 wizard). When
              the user has a brand logo set: shows an "Add logo to this
              frame" button. When not: shows a link to the My Brand page.
              Hidden once a logo is already on this frame (one logo per
              frame is the v1 cap). */}
          {onLogoOverlaysChange && currentLogos.length === 0 && (
            <div className="flex-shrink-0">
              {brandLogoUrl ? (
                <button
                  onClick={addLogoForCurrentFrame}
                  className="
                    w-full flex items-center gap-3 p-2.5 rounded-xl
                    border-2 border-dashed border-primary/40 bg-primary/5
                    hover:border-primary hover:bg-primary/10
                    transition-all cursor-pointer text-left
                  "
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-surface-dark/30 flex items-center justify-center flex-shrink-0">
                    <img
                      src={brandLogoUrl}
                      alt="Brand logo"
                      className="w-full h-full object-contain p-1"
                      draggable={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-primary">
                      Add brand logo
                    </p>
                    <p className="text-[11px] text-dark-muted leading-tight">
                      Places your logo in the corner — drag to reposition.
                    </p>
                  </div>
                  <i className="ri-add-line text-primary text-lg flex-shrink-0" />
                </button>
              ) : (
                <a
                  href="/brand"
                  className="
                    block p-3 rounded-xl border border-dashed border-surface-dark/40
                    bg-surface/40 hover:border-primary/40 hover:bg-surface
                    transition-colors text-left
                  "
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-white border border-surface-dark/30 flex items-center justify-center flex-shrink-0 text-dark-muted">
                      <i className="ri-image-line text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-dark">
                        No brand logo yet
                      </p>
                      <p className="text-[11px] text-dark-muted leading-tight">
                        Set one up in <span className="text-primary font-bold">My Brand</span> to stamp every post.
                      </p>
                    </div>
                    <i className="ri-arrow-right-up-line text-dark-muted" />
                  </div>
                </a>
              )}
            </div>
          )}

          {/* Layer list. Click a row to select; selection mirrors canvas click. */}
          <div className="flex-shrink-0 space-y-2">
            {currentOverlays.length === 0 && currentLogos.length === 0 ? (
              <div className="text-center py-4 bg-white rounded-xl border border-surface-dark/30">
                <p className="text-xs text-dark-muted">Apply a preset to add text</p>
              </div>
            ) : (
              <>
              {currentOverlays.map((overlay) => (
                <div
                  key={overlay.id}
                  onClick={() => setActiveId(overlay.id)}
                  className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all cursor-pointer ${
                    activeId === overlay.id ? 'border-primary bg-primary/5' : 'border-surface-dark/30 bg-white hover:border-primary/30'
                  }`}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: overlay.color + '20', color: overlay.color }}
                  >
                    T
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark truncate flex items-center gap-1">
                      {overlay.stackOnPrevious && (
                        <i
                          className="ri-link text-xs text-primary flex-shrink-0"
                          title="Coupled to layer above — reflows when previous layer's length changes"
                        />
                      )}
                      <span className="truncate">{overlay.text || 'Untitled'}</span>
                    </p>
                    <p className="text-xs text-dark-muted">
                      {overlay.fontSize}px · {overlay.fontWeight}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeOverlay(overlay.id); }}
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-dark-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              ))}

              {/* Logo rows in the layer list. Same row shape as text rows
                  for visual consistency — icon tile + name + delete. */}
              {currentLogos.map((logo) => (
                <div
                  key={logo.id}
                  onClick={() => setActiveId(logo.id)}
                  className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all cursor-pointer ${
                    activeId === logo.id ? 'border-primary bg-primary/5' : 'border-surface-dark/30 bg-white hover:border-primary/30'
                  }`}
                >
                  <div className="w-7 h-7 rounded-lg bg-white border border-surface-dark/30 overflow-hidden flex items-center justify-center flex-shrink-0">
                    <img
                      src={logo.src}
                      alt="Brand logo"
                      className="w-full h-full object-contain p-0.5"
                      draggable={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark truncate">
                      Brand logo
                    </p>
                    <p className="text-xs text-dark-muted">
                      {Math.round(logo.widthFrac * 100)}% size · {Math.round(logo.opacity * 100)}% opacity
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeLogo(logo.id); }}
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-dark-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              ))}
              </>
            )}
          </div>

          {/* Edit Layer panel — fills remaining height on xl; natural height when stacked. */}
          <div className="xl:flex-1 xl:min-h-0">
            {activeLogo ? (
              // Logo controls — minimal v1 (size + opacity sliders).
              // Position is set by drag; aspect is preserved.
              <div key={activeLogo.id} className="xl:h-full bg-white rounded-2xl border border-surface-dark/40 p-4 space-y-4 xl:overflow-y-auto">
                <div className="flex items-center gap-3 pb-3 border-b border-surface-dark/20">
                  <div className="w-10 h-10 rounded-lg bg-white border border-surface-dark/30 overflow-hidden flex items-center justify-center flex-shrink-0">
                    <img src={activeLogo.src} alt="Brand logo" className="w-full h-full object-contain p-1" draggable={false} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-dark">Brand logo</p>
                    <p className="text-[11px] text-dark-muted leading-tight">Drag on the preview to reposition.</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-dark-muted">Size</label>
                    <span className="text-xs text-dark">{Math.round(activeLogo.widthFrac * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min={5}
                    max={50}
                    step={1}
                    value={Math.round(activeLogo.widthFrac * 100)}
                    onChange={(e) => updateLogo(activeLogo.id, { widthFrac: Number(e.target.value) / 100 })}
                    className="w-full accent-primary h-1.5"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-dark-muted">Opacity</label>
                    <span className="text-xs text-dark">{Math.round(activeLogo.opacity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min={20}
                    max={100}
                    step={5}
                    value={Math.round(activeLogo.opacity * 100)}
                    onChange={(e) => updateLogo(activeLogo.id, { opacity: Number(e.target.value) / 100 })}
                    className="w-full accent-primary h-1.5"
                  />
                </div>

                {/* Color — three-way segmented control. Original keeps
                    the source PNG colors; Black + White recolor the
                    silhouette so the logo reads against any background
                    photo (dark scenes need a white stamp; light scenes
                    need black). Implemented via CSS filter in the
                    DOM preview and globalCompositeOperation='source-in'
                    in the canvas export path. */}
                <div>
                  <label className="block text-xs font-medium text-dark-muted mb-1.5">Color</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {([
                      { id: 'original', label: 'Color',  swatch: 'bg-gradient-to-br from-primary via-accent to-sky-400' },
                      { id: 'black',    label: 'Black',  swatch: 'bg-dark' },
                      { id: 'white',    label: 'White',  swatch: 'bg-white border border-surface-dark/40' },
                    ] as { id: LogoTint; label: string; swatch: string }[]).map((opt) => {
                      const isActive = (activeLogo.tint ?? 'original') === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => updateLogo(activeLogo.id, { tint: opt.id })}
                          className={`
                            flex flex-col items-center gap-1 p-2 rounded-xl border-2
                            transition-all cursor-pointer
                            ${isActive
                              ? 'border-primary bg-primary/5'
                              : 'border-surface-dark/30 bg-white hover:border-primary/40'}
                          `}
                        >
                          <span className={`w-6 h-6 rounded-full ${opt.swatch}`} />
                          <span className={`text-[10px] font-bold ${isActive ? 'text-primary' : 'text-dark'}`}>
                            {opt.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={() => removeLogo(activeLogo.id)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line" />
                  Remove logo
                </button>
              </div>
            ) : activeOverlay ? (
              <div key={activeOverlay.id} className="xl:h-full bg-white rounded-2xl border border-surface-dark/40 p-4 space-y-4 xl:overflow-y-auto">
                <div>
                  <label className="block text-xs font-medium text-dark-muted mb-1">Text Content</label>
                  <input
                    type="text"
                    defaultValue={activeOverlay.text}
                    onBlur={(e) => { if (e.target.value !== activeOverlay.text) updateOverlay(activeOverlay.id, { text: e.target.value }); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm text-dark focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
                    placeholder="Enter text…"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-dark-muted">Font Size</label>
                    <span className="text-xs text-dark">{activeOverlay.fontSize}px</span>
                  </div>
                  <input
                    type="range"
                    min={16}
                    max={200}
                    step={2}
                    defaultValue={activeOverlay.fontSize}
                    onMouseUp={(e) => updateOverlay(activeOverlay.id, { fontSize: Number((e.target as HTMLInputElement).value) })}
                    onTouchEnd={(e) => updateOverlay(activeOverlay.id, { fontSize: Number((e.target as HTMLInputElement).value) })}
                    className="w-full accent-primary h-1.5"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-dark-muted mb-1">Weight</label>
                    <div className="flex gap-1">
                      {([
                        { id: 'normal', label: 'R', title: 'Regular' },
                        { id: 'medium', label: 'M', title: 'Medium' },
                        { id: 'bold', label: 'B', title: 'Bold' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.id}
                          title={opt.title}
                          onClick={() => updateOverlay(activeOverlay.id, { fontWeight: opt.id })}
                          className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                            activeOverlay.fontWeight === opt.id ? 'bg-primary text-white' : 'bg-surface text-dark-muted hover:bg-surface-dark'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-dark-muted mb-1">Case</label>
                    <div className="flex gap-1">
                      {([
                        { id: 'none', label: 'Aa', title: 'As typed' },
                        { id: 'uppercase', label: 'AA', title: 'Uppercase' },
                        { id: 'lowercase', label: 'aa', title: 'Lowercase' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.id}
                          title={opt.title}
                          onClick={() => updateOverlay(activeOverlay.id, { textTransform: opt.id })}
                          className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                            activeOverlay.textTransform === opt.id ? 'bg-primary text-white' : 'bg-surface text-dark-muted hover:bg-surface-dark'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-dark-muted mb-1">Color</label>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {textColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => updateOverlay(activeOverlay.id, { color })}
                        aria-label={`Color ${color}`}
                        className={`w-7 h-7 rounded-full border-2 flex-shrink-0 transition-all cursor-pointer ${
                          activeOverlay.color === color ? 'border-primary scale-110' : 'border-surface-dark/30 hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="xl:h-full bg-white rounded-2xl border border-dashed border-surface-dark/40 p-6 flex items-center justify-center min-h-[100px]">
                <p className="text-xs text-dark-muted text-center">
                  {currentOverlays.length === 0
                    ? 'Pick a preset to get started'
                    : 'Select a layer above to edit'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions — wrapped in the same panel surface as the
          wizard's bottom-nav (see app/page.tsx) so step 7 visually
          matches the other steps' footer treatment. */}
      <div className="flex-shrink-0 mt-4 bg-surface-panel rounded-2xl border-2 border-white px-6 py-4 shadow-lg shadow-primary/15 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-dark hover:bg-surface transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Back
        </button>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Dev-only: "Save as Recreate template" button. Only renders
              when the parent (currently /appv2) passes onSaveAsRecreate.
              Dashed border + "DEV" pill so it reads as authoring tooling,
              not a user-facing action. */}
          {onSaveAsRecreate && (
            <button
              onClick={handleSaveAsRecreate}
              disabled={recreateSaveStatus === 'saving' || !activeImage}
              title="DEV: capture this composition as a reusable Recreate template"
              className={`inline-flex items-center gap-2 px-4 py-3 rounded-full border-2 border-dashed text-xs font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                recreateSaveStatus === 'saved'
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                  : recreateSaveStatus === 'error'
                  ? 'border-red-400 text-red-600 bg-red-50'
                  : 'border-fuchsia-400/60 text-fuchsia-700 hover:bg-fuchsia-50'
              }`}
            >
              <span className="hidden sm:inline-block px-1.5 py-0.5 rounded bg-fuchsia-200 text-fuchsia-900 text-[9px] font-black uppercase tracking-wider">
                Dev
              </span>
              {recreateSaveStatus === 'saving' && <><i className="ri-loader-4-line animate-spin text-base" />Capturing…</>}
              {recreateSaveStatus === 'saved' && <><i className="ri-check-line text-base" />Saved as Recreate</>}
              {recreateSaveStatus === 'error' && <><i className="ri-error-warning-line text-base" />Retry</>}
              {recreateSaveStatus === 'idle' && <><i className="ri-magic-line text-base" />Save as Recreate</>}
            </button>
          )}
          <button
            onClick={handleSaveToGallery}
            disabled={saveStatus === 'saving' || !activeImage}
            title="Save this composition to your gallery"
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border text-sm font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
              saveStatus === 'saved'
                ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                : saveStatus === 'error'
                ? 'border-red-400 text-red-600 bg-red-50'
                : 'border-surface-dark text-dark hover:border-primary/40 hover:text-primary'
            }`}
          >
            {saveStatus === 'saving' && <><i className="ri-loader-4-line animate-spin text-base" />Saving…</>}
            {saveStatus === 'saved' && <><i className="ri-check-line text-base" />Saved</>}
            {saveStatus === 'error' && <><i className="ri-error-warning-line text-base" />Retry</>}
            {saveStatus === 'idle' && <><i className="ri-heart-3-line text-base" />Save to My Creatives</>}
          </button>
          {isFree ? (
            <button
              onClick={() => navigate('/checkout')}
              title="Free trial doesn't include export — upgrade to download your posts"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-amber-500/20 whitespace-nowrap"
            >
              <i className="ri-vip-crown-line text-lg" />
              Upgrade to Download
            </button>
          ) : (
            <>
              {/* Carousel-only: bundle all 4 frames into a single zip. Sits
                  to the LEFT of the primary Download button as a secondary
                  action — same outline treatment as Save to Gallery so the
                  visual hierarchy is: outline (secondary) → outline → solid
                  primary (the single-frame download). */}
              {isCarousel && (
                <button
                  onClick={handleDownloadAll}
                  disabled={
                    !activeImage ||
                    downloadAllStatus === 'zipping' ||
                    generatedImages.length === 0
                  }
                  title="Download all 4 frames as a zip"
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border text-sm font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                    downloadAllStatus === 'error'
                      ? 'border-red-400 text-red-600 bg-red-50'
                      : 'border-surface-dark text-dark hover:border-primary/40 hover:text-primary'
                  }`}
                >
                  {downloadAllStatus === 'zipping' && (
                    <><i className="ri-loader-4-line animate-spin text-base" />Zipping…</>
                  )}
                  {downloadAllStatus === 'error' && (
                    <><i className="ri-error-warning-line text-base" />Retry</>
                  )}
                  {downloadAllStatus === 'idle' && (
                    <><i className="ri-folder-zip-line text-base" />Download all (zip)</>
                  )}
                </button>
              )}
              <button
                onClick={handleDownload}
                disabled={!activeImage}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-primary/15 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-download-2-line text-lg" />
                Download
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
